<div class="edit-claim-modal" *transloco="let t; prefix: 'accidents.modals.editClaim'">
  <div class="edit-claim-modal__header">
    <h3 class="edit-claim-modal__title">{{ t('title') }}</h3>
    <button type="button" class="icon-btn" [attr.aria-label]="'common.close' | transloco" (click)="cancel()" [disabled]="isSubmitting">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M18 6L6 18M6 6l12 12"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
        </path>
      </svg>
    </button>
  </div>

  <form [formGroup]="claimForm" class="edit-claim-modal__form">
    <div class="form-step">
      <div class="form-group">
        <label for="assetName" class="form-label">{{ t('assetName') }}</label>
        <input
          type="text"
          id="assetName"
          formControlName="assetName"
          class="form-control"
          [placeholder]="t('assetNamePlaceholder')">
      </div>

      <div class="form-group">
        <label for="claimNumber" class="form-label">{{ t('claimNumber') }}</label>
        <input
          type="text"
          id="claimNumber"
          formControlName="claimNumber"
          class="form-control">
      </div>

      <div class="form-group">
        <label class="form-label">{{ t('dateFiled') }}</label>
        <app-flatpickr-date-picker
          [(value)]="dateFiled"
          [disabled]="true"
          [allowPastDates]="true">
        </app-flatpickr-date-picker>
      </div>

      <div class="form-group">
        <label class="form-label">{{ t('category') }}</label>
        <div ngbDropdown class="dropdown-wrapper" [autoClose]="true">
          <button type="button" class="form-control dropdown-toggle" ngbDropdownToggle>
            <div class="dropdown-inner">
              <span>{{ categoryLabel }}</span>
              <i class="ph ph-caret-down"></i>
            </div>
          </button>
          <div ngbDropdownMenu class="dropdown-menu">
            @if (isLoadingCategories) {
              <div class="dropdown-loading">
                <span class="spinner-border spinner-border-sm"></span>
              </div>
            } @else {
              @for (category of categories; track category.id) {
                <button type="button" class="dropdown-item" (click)="onCategorySelect(category)" ngbDropdownItem>
                  {{ category.localizedName || category.name }}
                </button>
              }
            }
          </div>
        </div>
      </div>

      <div class="form-group form form-group--full">
        <label for="insuranceName" class="form-label">{{ t('insuranceProvider') }}</label>
        <input
          type="text"
          id="insuranceName"
          formControlName="insuranceName"
          class="form-control"
          [placeholder]="t('selectInsuranceProvider')">
      </div>

      <div class="form-group">
        <label for="settledAmount" class="form-label">{{ t('settledAmount') }}</label>
        <div class="input-with-prefix">
          <span class="input-prefix">{{ currencySymbol }}</span>
          <input
            type="text"
            id="settledAmount"
            formControlName="settledAmount"
            class="form-control"
            [placeholder]="t('settledAmountPlaceholder')">
        </div>
      </div>

      <div class="form-group">
        <label class="form-label">{{ t('claimStatus') }}</label>
        <div ngbDropdown class="dropdown-wrapper" [autoClose]="true">
          <button type="button" class="form-control dropdown-toggle" ngbDropdownToggle>
            <div class="dropdown-inner">
              <span>{{ statusLabel }}</span>
              <i class="ph ph-caret-down"></i>
            </div>
          </button>
          <div ngbDropdownMenu class="dropdown-menu">
            @for (status of statuses; track status.id) {
              <button type="button" class="dropdown-item" (click)="onStatusSelect(status)" ngbDropdownItem>
                {{ status.name }}
              </button>
            }
          </div>
        </div>
      </div>

      <div class="form-group form-group--full">
        <div class="attachments-header">
          <label class="form-label">{{ t('attachFilesOrPhotos') }}</label>
          <span class="attachments-header__count">{{ t('attachmentCount', { count: keptAttachmentCount }) }}</span>
        </div>

        <div class="attachments-grid">
          <div
            class="attachment-tile attachment-tile--add"
            [class.drag-over]="isDragOver"
            [class.attachment-tile--disabled]="isSubmitting"
            (dragover)="onDragOver($event)"
            (dragleave)="onDragLeave($event)"
            (drop)="onDrop($event)"
            (click)="openFilePicker(fileInput)">
            <input
              type="file"
              #fileInput
              multiple
              accept="image/*,video/*,.pdf"
              (change)="onFileSelect($event)"
              hidden>
            <span class="attachment-tile__add-text">{{ t('dragAndDrop') }}</span>
          </div>

          @for (attachment of newAttachments; track attachment.reference) {
            <div class="attachment-tile" [title]="attachment.name">
              @if (attachment.previewUrl) {
                <img class="attachment-tile__image" [src]="attachment.previewUrl" [alt]="attachment.name">
              } @else {
                <div class="attachment-tile__file">
                  <i class="ph ph-file"></i>
                  <span>{{ attachment.name }}</span>
                </div>
              }

              @if (attachment.isUploading) {
                <div class="attachment-tile__overlay">
                  <span>{{ attachment.uploadProgress }}%</span>
                  <div class="attachment-tile__progress">
                    <div class="attachment-tile__progress-bar" [style.width.%]="attachment.uploadProgress"></div>
                  </div>
                </div>
              } @else if (attachment.uploadFailed) {
                <div class="attachment-tile__overlay attachment-tile__overlay--failed">
                  <span>{{ 'accidents.modals.reportIncident.uploadFailed' | transloco }}</span>
                </div>
              }

              <div class="attachment-tile__actions">
                @if (attachment.uploadFailed && !attachment.isUploading) {
                  <button type="button" class="attachment-tile__action" [title]="t('retry')" (click)="retryUpload(attachment)" [disabled]="isSubmitting">
                    <i class="ph ph-arrow-clockwise"></i>
                  </button>
                }
                @if (!attachment.isUploading) {
                  <button type="button" class="attachment-tile__action" [title]="t('download')" (click)="downloadNewAttachment(attachment)">
                    <i class="ph ph-download-simple"></i>
                  </button>
                }
                <button type="button" class="attachment-tile__action" [title]="t('delete')" (click)="removeNewAttachment(attachment)" [disabled]="isSubmitting">
                  <i class="ph ph-trash"></i>
                </button>
              </div>
            </div>
          }

          @for (attachment of existingAttachments; track attachment.documentId) {
            <div class="attachment-tile" [class.attachment-tile--marked]="isMarkedForDeletion(attachment)" [title]="attachment.name">
              @if (isImage(attachment.mediaType, attachment.extension)) {
                <img class="attachment-tile__image" [src]="attachment.url" [alt]="attachment.name">
              } @else {
                <div class="attachment-tile__file">
                  <i class="ph ph-file"></i>
                  <span>{{ attachment.name }}</span>
                </div>
              }

              @if (isMarkedForDeletion(attachment)) {
                <div class="attachment-tile__overlay attachment-tile__overlay--failed">
                  <span>{{ t('markedForDeletion') }}</span>
                </div>
              }

              <div class="attachment-tile__actions">
                @if (!isMarkedForDeletion(attachment)) {
                  <button type="button" class="attachment-tile__action" [title]="t('download')" (click)="downloadExistingAttachment(attachment)">
                    <i class="ph ph-download-simple"></i>
                  </button>
                  <button type="button" class="attachment-tile__action" [title]="t('delete')" (click)="toggleExistingAttachmentDeletion(attachment)" [disabled]="isSubmitting">
                    <i class="ph ph-trash"></i>
                  </button>
                } @else {
                  <button type="button" class="attachment-tile__action" [title]="t('undo')" (click)="toggleExistingAttachmentDeletion(attachment)" [disabled]="isSubmitting">
                    <i class="ph ph-arrow-counter-clockwise"></i>
                  </button>
                }
              </div>
            </div>
          }
        </div>

        @if (attachmentError) {
          <span class="attachment-error">{{ attachmentError }}</span>
        }
      </div>
    </div>
  </form>

  <div class="edit-claim-modal__attachments-meta">
    <span>{{ t('maxFileSize') }}</span>
  </div>

  <div class="edit-claim-modal__actions">
    <button type="button" class="btn-cancel" (click)="cancel()" [disabled]="isSubmitting">
      <span>{{ 'common.cancel' | transloco }}</span>
    </button>
    <button type="button" class="btn-save" (click)="save()" [disabled]="!isFormValid || isSubmitting">
      <span>{{ t('saveChanges') }}</span>
    </button>
  </div>
</div>
